import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'multiIndicatorRingGauge',
  name_zh: '多指标环状仪表盘',
  name_en: 'Multi-indicator Ring Gauge',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount.length < 5,
    data: amount => amount == 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.ARRAY
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
  };
}

export const multiIndicatorRingGauge = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util: utils, hook: hooks } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hooks.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x }
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hooks.parseDataMetaInfo(data);
    const hasSubKLine = hooks.layout(api).mainView.hasHXKLineLayer;

    // * ------------------------- data
    const HEIGHT = 32;
    const MARGIN = 12;
    const PADDING = 8;
    const radius = (Math.min(chartViewRect.width, chartViewRect.height) - HEIGHT) / 2;
    const radius1 = radius - HEIGHT / 2;
    const offsetY =
      HEIGHT +
      Math.max(0, chartViewRect.height - HEIGHT - (hasSubKLine ? radius : radius1) * 2) * 0.5;
    const centerY = offsetY + radius1;
    let xPosition = 0;
    const gaugeData = x.map(gauge => {
      const res = +data.values[0][gauge];
      const result = hooks.formatDataNumberValue(res);
      const name = `${gauge}${result.unit ? `(${result.unit})` : ''}`;
      const nameTextWidth = utils.canvas.measureTextWidth(
        name,
        presetOption.textStyle.fontSize,
        presetOption.textStyle.fontFamily
      );
      const valueTextWidth = utils.canvas.measureTextWidth(+result.value, 12);
      const detailWidth = valueTextWidth + PADDING;
      const nameX = xPosition + nameTextWidth / 2;
      const detailX = nameX + nameTextWidth / 2 + detailWidth / 2 + MARGIN / 3 + PADDING * 1.25;

      xPosition = detailX + detailWidth + MARGIN;

      return {
        value: +result.value,
        name,
        title: {
          offsetCenter: [-chartViewRect.width / 2 + nameX, -centerY + HEIGHT / 2 - PADDING / 2]
        },
        detail: {
          width: detailWidth,
          valueAnimation: true,
          offsetCenter: [-chartViewRect.width / 2 + detailX, -centerY + HEIGHT / 2 - PADDING / 2]
        }
      };
    });

    // * ------------------------- chart
    return {
      legend: {
        data: []
      },
      xAxis: [],
      yAxis: [],
      tooltip: {
        ...presetOption?.tooltip,
        trigger: 'axis'
      },
      textStyle: presetOption?.textStyle,
      series: [
        {
          type: 'gauge',
          center: ['50%', centerY],
          radius: radius1,
          startAngle: 90,
          endAngle: -270,
          pointer: {
            show: false
          },
          progress: {
            show: true,
            overlap: false,
            clip: false
          },
          axisLine: {
            lineStyle: {
              width: 40
            }
          },
          splitLine: {
            show: false,
            distance: 0,
            length: 10
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            show: false
          },
          data: gaugeData,
          title: {
            show: true,
            fontSize: 12,
            lineHeight: 12
          },
          detail: {
            show: true,
            height: 12,
            fontWeight: 'normal',
            fontSize: 12,
            lineHeight: 12,
            padding: [2, 8, 2, 8],
            color: 'inherit',
            borderColor: 'inherit',
            borderRadius: HEIGHT,
            borderWidth: 1,
            formatter: value => {
              const result = hooks.formatDataNumberValue(value, {
                type: 'table',
                unit: dataMetaInfo[x]?.unit
              });

              return result.value + result.unit;
            }
          }
        }
      ]
    };
  }
});
